import React from 'react';
import { Bell, MapPin, Plus, Trash, SpinnerGap, PaperPlaneTilt, FloppyDisk } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';

export default function JobAlertsTab({
  jobAlertPrefs, setJobAlertPrefs, loadingAlerts, savingAlerts,
  newAlertSkill, setNewAlertSkill, addAlertSkill, removeAlertSkill,
  newAlertLocation, setNewAlertLocation, addAlertLocation, removeAlertLocation,
  handleSendTestAlert, handleSaveJobAlerts
}) {
  return (
    <div className="space-y-6" data-testid="job-alerts-tab">
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Job Alerts</h2>
            <p className="text-gray-500 mt-1">Get notified when jobs matching your preferences are posted</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">{jobAlertPrefs.enabled ? 'Enabled' : 'Disabled'}</span>
            <button
              onClick={() => setJobAlertPrefs(prev => ({ ...prev, enabled: !prev.enabled }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${jobAlertPrefs.enabled ? 'bg-orange-500' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${jobAlertPrefs.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        {loadingAlerts ? (
          <div className="text-center py-12">
            <SpinnerGap size={40} weight="bold" className="text-[#2B4C6F] animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Loading preferences...</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium text-gray-700">Skills to Match</Label>
              <p className="text-xs text-gray-500 mb-2">You&apos;ll be notified about jobs requiring these skills</p>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add a skill (e.g., Plumbing, Electrical)"
                  value={newAlertSkill}
                  onChange={(e) => setNewAlertSkill(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addAlertSkill()}
                />
                <Button onClick={addAlertSkill} variant="outline">
                  <Plus size={16} weight="bold" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {jobAlertPrefs.skills.map((skill) => (
                  <Badge key={`skill-${skill}`} variant="secondary" className="px-3 py-1 flex items-center gap-1">
                    {skill}
                    <button onClick={() => removeAlertSkill(skill)} className="ml-1 hover:text-red-500">
                      <Trash size={12} weight="bold" />
                    </button>
                  </Badge>
                ))}
                {jobAlertPrefs.skills.length === 0 && (
                  <p className="text-sm text-gray-400">No skills added. Add skills to get relevant job alerts.</p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-700">Preferred Locations</Label>
              <p className="text-xs text-gray-500 mb-2">Get alerts for jobs in these areas</p>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add a location (e.g., Los Angeles, CA)"
                  value={newAlertLocation}
                  onChange={(e) => setNewAlertLocation(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addAlertLocation()}
                />
                <Button onClick={addAlertLocation} variant="outline">
                  <Plus size={16} weight="bold" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {jobAlertPrefs.locations.map((loc) => (
                  <Badge key={`loc-${loc}`} variant="secondary" className="px-3 py-1 flex items-center gap-1">
                    <MapPin size={12} weight="bold" />
                    {loc}
                    <button onClick={() => removeAlertLocation(loc)} className="ml-1 hover:text-red-500">
                      <Trash size={12} weight="bold" />
                    </button>
                  </Badge>
                ))}
                {jobAlertPrefs.locations.length === 0 && (
                  <p className="text-sm text-gray-400">No locations added. You&apos;ll get alerts for all locations.</p>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="min-budget">Minimum Budget ($)</Label>
                <Input
                  id="min-budget"
                  type="number"
                  placeholder="e.g., 500"
                  value={jobAlertPrefs.min_budget}
                  onChange={(e) => setJobAlertPrefs(prev => ({ ...prev, min_budget: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="max-budget">Maximum Budget ($)</Label>
                <Input
                  id="max-budget"
                  type="number"
                  placeholder="e.g., 10000"
                  value={jobAlertPrefs.max_budget}
                  onChange={(e) => setJobAlertPrefs(prev => ({ ...prev, max_budget: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Alert Frequency</Label>
              <div className="flex gap-3">
                {[
                  { value: 'instant', label: 'Instant', desc: 'Get notified immediately' },
                  { value: 'daily', label: 'Daily Digest', desc: 'Once per day summary' },
                  { value: 'weekly', label: 'Weekly Digest', desc: 'Once per week summary' }
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => setJobAlertPrefs(prev => ({ ...prev, frequency: option.value }))}
                    className={`flex-1 p-4 rounded-lg border-2 text-left transition-all ${jobAlertPrefs.frequency === option.value ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <p className={`font-medium ${jobAlertPrefs.frequency === option.value ? 'text-orange-700' : 'text-gray-900'}`}>{option.label}</p>
                    <p className="text-xs text-gray-500 mt-1">{option.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <Button variant="outline" onClick={handleSendTestAlert} disabled={!jobAlertPrefs.enabled}>
                <PaperPlaneTilt size={16} weight="bold" className="mr-2" />
                Send Test Alert
              </Button>
              <Button onClick={handleSaveJobAlerts} disabled={savingAlerts} className="bg-orange-500 hover:bg-orange-600">
                {savingAlerts ? (
                  <SpinnerGap size={20} weight="bold" className="mr-2 animate-spin" />
                ) : (
                  <FloppyDisk size={16} weight="bold" className="mr-2" />
                )}
                Save Preferences
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-6 text-white">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <Bell size={24} weight="bold" />
          </div>
          <div>
            <h3 className="font-bold text-lg">How Job Alerts Work</h3>
            <ul className="mt-2 space-y-1 text-blue-100 text-sm">
              <li>• We match new jobs with your skills and location preferences</li>
              <li>• Receive email notifications based on your chosen frequency</li>
              <li>• Quickly apply to jobs before others see them</li>
              <li>• Update your preferences anytime to refine your alerts</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
